// src/utils/americano.ts — Americanon kierrosgeneraattori
//
// Yksi totuus ottelujen arvonnalle. Logiikka oli ennen AdminView'n sisällä
// yhtenä silmukkana, joka loi koko kierron kerralla. Se riitti niin kauan
// kuin ilta pelattiin loppuun kertaalleen, mutta 11 pisteen peleissä
// kierto loppuu kesken illan: ympyrämenetelmä tuottaa täsmälleen
// `pelaajat − 1` kierrosta, eli neljällä pelaajalla kolme ottelua.
//
// Nyt yhden kierroksen arvonta on oma funktio, jota sekä koko kierron
// generointi että "Lisää kierros" -nappi kutsuvat. Niin liitetty kierros
// ei voi poiketa muodoltaan generoiduista.

/** Taytepelaaja parittomalle maaralle. Ei paady otteluihin. */
const BYE = "BYE";

/** Yhden ottelun joukkueet. Kierrosnumero annetaan vasta tallennuksessa. */
export interface AmericanoGame {
  team1: [string, string];
  team2: [string, string];
}

/**
 * Montako erillista kierrosta ympyramenetelma tuottaa.
 *
 * Pariton pelaajamaara taytetaan BYE:lla, joten kierroksia on yhta
 * vahemman kuin pelaajia pyoristettyna ylos parilliseen.
 *
 * @param playerCount vahvistettujen pelaajien maara.
 * @return kierrosten maara, tai 0 jos pelaajia on alle neljan.
 */
export function americanoRoundCount(playerCount: number): number {
  if (playerCount < 4) return 0;
  return (playerCount % 2 === 0 ? playerCount : playerCount + 1) - 1;
}

/**
 * Yhden kierroksen ottelut.
 *
 * Ympyramenetelma: ensimmainen pelaaja pysyy paikallaan ja loput
 * kiertavat yhden askeleen kierrosta kohden. Vastakkaisista paista
 * poimitut pelaajat ovat pari, ja kaksi peraikkaista paria muodostavat
 * yhden ottelun.
 *
 * Otteluita rajataan kenttien maaralla: nelja pelaajaa kenttaa kohden,
 * joten kuudella pelaajalla pelaa nelja ja kaksi lepaa. Lepaajat
 * vaihtuvat kierroksittain, koska kierto siirtyy.
 *
 * @param names vahvistettujen pelaajien nimet.
 * @param roundIndex kierroksen jarjestysluku nollasta alkaen. Suuremmat
 *   arvot kiertavat alkuun: `americanoRoundCount`:n jalkeen pariutukset
 *   toistuvat samassa jarjestyksessa. Se on tarkoitus — toinen kierto on
 *   ennustettava eika yllata pelaajia.
 * @return kierroksen ottelut, tai tyhja lista jos pelaajia on alle neljan.
 */
export function americanoRound(names: string[], roundIndex: number): AmericanoGame[] {
  const playerCount = names.length;
  if (playerCount < 4) return [];

  const rounds = americanoRoundCount(playerCount);
  const round = ((roundIndex % rounds) + rounds) % rounds;

  const courtsPerRound = Math.floor(playerCount / 4);
  const ps = playerCount % 2 === 0 ? [...names] : [...names, BYE];
  const M = ps.length;

  const arranged: string[] = [ps[0]];
  for (let i = 1; i < M; i++) {
    arranged.push(ps[1 + ((i - 1 + round) % (M - 1))]);
  }

  const pairs: [string, string][] = [];
  for (let k = 0; k < M / 2; k++) {
    const p1 = arranged[k];
    const p2 = arranged[M - 1 - k];
    if (p1 !== BYE && p2 !== BYE) pairs.push([p1, p2]);
  }

  const out: AmericanoGame[] = [];
  for (let g = 0; g + 1 < pairs.length && out.length < courtsPerRound; g += 2) {
    out.push({ team1: pairs[g], team2: pairs[g + 1] });
  }
  return out;
}


// ── Puolten tasapuolisuus ────────────────────────────────────
//
// Kentan kaksi paata eivat ole aina samanarvoisia: Vuoksenniskalla
// toisen takakentan takana on seina lahella, joten siella pelaaminen on
// huonompi arpa. Ympyramenetelma ei tienyt siita mitaan, ja koska se
// pitaa ensimmaisen pelaajan paikallaan, sama pelaaja paatyi kierros
// toisensa jalkeen samaan paatyyn.
//
// Korjaus ei tarvitse tietoa siita kumpi paa on huono. Riittaa etta
// puolet jakautuvat tasaisesti: kun jokainen pelaa molempia paita yhta
// usein, kukaan ei joudu jatkuvasti huonolle puolelle riippumatta
// siita kumpi se on. Siksi tassa ei ole yhtaan kenttakohtaista
// asetusta.
//
// `team1` on puoli A ja `team2` puoli B. Ottelun kaantaminen on
// turvallista: tulos ja pisteet ovat symmetrisia, eika puolella ole
// muuta merkitysta sovelluksessa.

/** Montako kertaa pelaaja on pelannut kummallakin puolella. */
export interface SideTally {
  a: number;
  b: number;
}

/**
 * Pelattujen ja arvottujen ottelujen puolijakauma pelaajittain.
 *
 * @param games ottelut, myos tuloksettomat: puoli on jaettu silloinkin.
 * @return jokaisen pelaajan puolilaskurit nimella avainnettuna.
 */
export function sideTallies(
  games: { team1: string[]; team2: string[] }[]
): Record<string, SideTally> {
  const out: Record<string, SideTally> = {};
  const bump = (name: string, key: "a" | "b") => {
    const t = out[name] ?? (out[name] = {a: 0, b: 0});
    t[key]++;
  };
  games.forEach(g => {
    g.team1.forEach(n => bump(n, "a"));
    g.team2.forEach(n => bump(n, "b"));
  });
  return out;
}

/** Pelaajan epatasapaino: positiivinen = liikaa puolta A. */
function bias(tallies: Record<string, SideTally>, name: string): number {
  const t = tallies[name];
  return t ? t.a - t.b : 0;
}

/**
 * Epatasapainon hinta neliosummana.
 *
 * Nelio on tassa olennainen eika kosmetiikkaa: pelkka summa antaisi
 * parille, jossa toinen on +2 ja toinen −2, hinnan nolla, ja juuri se
 * tilanne jaisi korjaamatta. Neliolla yhden pelaajan kolmas perakkainen
 * sama puoli maksaa enemman kuin kahden pelaajan yksi.
 *
 * @param tallies puolilaskurit.
 * @param names pelaajat jotka otetaan mukaan hintaan.
 * @return neliosumma.
 */
function cost(tallies: Record<string, SideTally>, names: string[]): number {
  return names.reduce((s, n) => {
    const d = bias(tallies, n);
    return s + d * d;
  }, 0);
}

/** Kirjaa ottelun puolet laskureihin, tai peruu ne kun delta on −1. */
function apply(
  tallies: Record<string, SideTally>,
  game: AmericanoGame,
  delta: 1 | -1
): void {
  const touch = (name: string, key: "a" | "b") => {
    const t = tallies[name] ?? (tallies[name] = {a: 0, b: 0});
    t[key] += delta;
  };
  game.team1.forEach(n => touch(n, "a"));
  game.team2.forEach(n => touch(n, "b"));
}

/** Ottelu toisin pain. */
function flip(g: AmericanoGame): AmericanoGame {
  return {team1: g.team2, team2: g.team1};
}

/**
 * Jakaa puolet niin, etta kukaan ei pelaa jatkuvasti samassa paassa.
 *
 * Kaksi vaihetta. Ensin jokainen ottelu jarjestyksessa kaannetaan jos se
 * pienentaa epatasapainoa, ja sen jalkeen koko lista kaydaan uudelleen
 * lapi niin kauan kuin yksittainen kaanto viela parantaa tulosta. Pelkka
 * ensimmainen vaihe ei riita, koska alkupaan valinta nakyy vasta lopussa.
 *
 * Aiemmin pelatut ottelut annetaan `history`ssa eika niita kaanneta:
 * "Lisaa kierros" tasaa siis uuden kierroksen sen mukaan mita illassa on
 * jo pelattu.
 *
 * Nelja pelaajaa on rajatapaus: kolmen kierroksen kierto on
 * taydellisesti tasattavissa vasta kun se on pelattu kahdesti. Kolmella
 * kierroksella joku pelaa kaikki kolme samassa paassa, eika se ole
 * jaannos vaan seuraus siita etta parit ovat toistensa taydennyksia.
 *
 * @param games tasattavat ottelut, kierrosjarjestyksessa.
 * @param history jo pelatut ottelut, joita ei kaanneta.
 * @return ottelut samassa jarjestyksessa, puolet tasattuina.
 */
export function balanceSides(
  games: AmericanoGame[],
  history: { team1: string[]; team2: string[] }[] = []
): AmericanoGame[] {
  const tallies = sideTallies(history);
  const out = [...games];

  // Vaihe 1: ahne kulku eteenpain.
  out.forEach((g, i) => {
    const names = [...g.team1, ...g.team2];
    apply(tallies, g, 1);
    const keep = cost(tallies, names);
    apply(tallies, g, -1);

    const swapped = flip(g);
    apply(tallies, swapped, 1);
    const swap = cost(tallies, names);
    apply(tallies, swapped, -1);

    // Tasatilanteessa pidetaan arvonnan oma jarjestys, jotta tulos on
    // sama joka kerta samoilla pelaajilla.
    const pick = swap < keep ? swapped : g;
    out[i] = pick;
    apply(tallies, pick, 1);
  });

  // Vaihe 2: paikallinen parannus. Raja on varmuuden vuoksi, ei siksi
  // etta kierto tiedettaisiin mahdolliseksi.
  for (let pass = 0; pass < 20; pass++) {
    let improved = false;
    out.forEach((g, i) => {
      const names = [...g.team1, ...g.team2];
      const before = cost(tallies, names);
      const swapped = flip(g);
      apply(tallies, g, -1);
      apply(tallies, swapped, 1);
      if (cost(tallies, names) < before) {
        out[i] = swapped;
        improved = true;
      } else {
        apply(tallies, swapped, -1);
        apply(tallies, g, 1);
      }
    });
    if (!improved) break;
  }

  return out;
}

/**
 * Suurin yksittaisen pelaajan epatasapaino.
 *
 * Kayttoliittyma tarvitsee taman kertoakseen milloin tasaus ei riita.
 * Vaiteliaisuus on tassa pahempi kuin itse epatasapaino: pelaaja joka
 * nakee olevansa kolmannen kerran samassa paassa paattelee etta arvonta
 * on rikki, eika hanella ole mitaan keinoa tietaa ettei ole.
 *
 * @param games ottelut.
 * @return suurin |puoli A − puoli B| yhdella pelaajalla, tai 0.
 */
export function worstSideBias(
  games: { team1: string[]; team2: string[] }[]
): number {
  const t = sideTallies(games);
  const arvot = Object.values(t).map(x => Math.abs(x.a - x.b));
  return arvot.length === 0 ? 0 : Math.max(...arvot);
}

/**
 * Kiertaa pelaajalistan tapahtumakohtaisesti.
 *
 * Ympyramenetelma pitaa listan ensimmaisen pelaajan paikallaan, ja siita
 * seuraa muutakin kuin pariutukset: nelja pelaajaa ja kolme kierrosta ei
 * ole tasattavissa lainkaan (kaikki kahdeksan puolijakoa jattavat jonkun
 * kolmesti samaan paahan), ja se joku on aina listan ensimmainen.
 *
 * Ilman tata se olisi siis joka ilta sama ihminen — ei huono tuuri vaan
 * jarjestelmallinen vinouma. Kierto ei poista epatasapainoa, koska se on
 * rakenteellinen, mutta se siirtaa sen eri pelaajalle joka illassa.
 *
 * Siemen on tapahtuman tunnus: sama ilta antaa aina saman kierron, joten
 * "Lisaa kierros" jatkaa samasta kierrosta eika sekoita pakkaa kesken
 * illan.
 *
 * @param names pelaajat ilmoittautumisjarjestyksessa.
 * @param seed tapahtuman tunnus.
 * @return sama lista kierrettyna.
 */
export function rotateForEvent(names: string[], seed: string): string[] {
  if (names.length < 2) return [...names];
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const k = ((h % names.length) + names.length) % names.length;
  return [...names.slice(k), ...names.slice(0, k)];
}

/**
 * Montako kierrosta puuttuu taydesta kierrosta.
 *
 * Nelja pelaajaa tarkoittaa kolmen kierroksen kiertoa, jossa jokainen
 * pelaa jokaisen kanssa tasan kerran. Viiden kierroksen illassa kaksi
 * paria ehtii yhteen kahdesti ja yksi kerran — ilta ei ole tasapuolinen,
 * vaikka kaikki pelaisivat yhta monta ottelua.
 *
 * Klubin paatos 2.11.2026: illassa lasketaan taydet kierrot. Sovellus
 * kertoo taman illan aikana, jotta vajaita iltoja ei tarvitse ratkoa
 * jalkikateen — se oli se vaihtoehto joka olisi vienyt pelaajilta koko
 * illan, lasnaolopisteineen.
 *
 * @param games illan ottelut; kierrosnumero luetaan `round`-kentasta.
 * @return montako kierrosta viela tarvitaan, tai 0 jos kierto on taysi.
 */
export function roundsToCompleteCycle(
  games: { round?: number; team1: string[]; team2: string[] }[]
): number {
  const names = new Set<string>();
  let rounds = 0;
  games.forEach(g => {
    g.team1.forEach(n => names.add(n));
    g.team2.forEach(n => names.add(n));
    rounds = Math.max(rounds, g.round ?? 0);
  });
  const cycle = americanoRoundCount(names.size);
  if (cycle === 0 || rounds === 0) return 0;
  return (cycle - (rounds % cycle)) % cycle;
}

/** Illan otteluiden jako taysiin kiertoihin ja kesken jaaneeseen hantaan. */
export interface RotationSplit<T> {
  /** Taysiin kiertoihin kuuluvat ottelut. Nama lasketaan liigaan. */
  counted: T[];
  /** Kesken jaaneelta kierrolta pudonneet ottelut. */
  dropped: T[];
  /** Pelattuja kierroksia. */
  rounds: number;
  /** Yhden tayden kierron pituus kierroksina, tai 0 jos sita ei ole. */
  cycle: number;
}

/**
 * Taysiin kiertoihin kuuluvat ottelut, ja ne jotka jaivat kierron ulkopuolelle.
 *
 * Klubin paatos 2.11.2026: liigaan lasketaan taydet kierrokset. Nelja
 * pelaajaa ja kolme kierrosta tarkoittaa, etta jokainen on pelannut
 * kaikkien kanssa tasan kerran. Viides kierros aloittaa saman kierron
 * uudelleen, joten osa pariutuksista toistuu ja osa ei — 25.10. illassa
 * pelattiin viisi ottelua ja 10.10. nelja, ja juuri siita pelaajat
 * huomasivat taulukon olevan vinossa.
 *
 * Tama **ei poista tuloksia mistaan**. Illan oma taulukko nayttaa kaikki
 * pelatut ottelut niin kuin ennenkin; vain kauden pisteisiin otetaan
 * taydet kierrokset. Pudonneet kerrotaan erikseen, jotta mikaan ei putoa
 * hiljaa (`LeagueNight.dropped`).
 *
 * Kasin lisatylla ottelulla ei ole kierrosta. Jos illassa on kierroksia,
 * kierroksettomat jaavat laskennan ulkopuolelle samasta syysta kuin
 * kesken jaanyt hanta: ne eivat kuulu kiertoon. Jos illassa ei ole
 * yhtaan kierrosta (pelkkia kasin lisattyja), kiertoa ei ole olemassa
 * eika mitaan rajata.
 *
 * @param games illan ottelut.
 * @return jako laskettaviin ja pudonneisiin.
 */
export function completeRotationGames<T extends { round?: number; team1: string[]; team2: string[] }>(
  games: T[],
): RotationSplit<T> {
  const names = new Set<string>();
  let rounds = 0;
  games.forEach(g => {
    g.team1.forEach(n => names.add(n));
    g.team2.forEach(n => names.add(n));
    rounds = Math.max(rounds, g.round ?? 0);
  });
  const cycle = americanoRoundCount(names.size);
  // Ei kiertoa (alle nelja pelaajaa) tai ei kierroksia (kasin lisatty ilta):
  // ei mitaan rajattavaa.
  if (cycle === 0 || rounds === 0) return { counted: [...games], dropped: [], rounds, cycle };

  const full = Math.floor(rounds / cycle) * cycle;
  const counted: T[] = [];
  const dropped: T[] = [];
  games.forEach(g => {
    const r = g.round ?? 0;
    (r >= 1 && r <= full ? counted : dropped).push(g);
  });
  return { counted, dropped, rounds, cycle };
}
